import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { UserPlus, Mail, Phone, User, Building, Store, MapPin, Wallet } from 'lucide-react';
import { authService } from '../services/authService';
import { useAuthStore } from '../store/authStore';
import { isValidPhone } from '../utils/phoneValidation';

type ProfilType = 'particulier' | 'vendeur_social';
type CodMoyen = 'om' | 'momo' | 'cash';

const COD_MOYENS: { value: CodMoyen; label: string }[] = [
  { value: 'momo', label: 'MTN MoMo' },
  { value: 'om', label: 'Orange Money' },
  { value: 'cash', label: 'Espèces' },
];

export const RegisterExpediteur: React.FC = () => {
  const navigate = useNavigate();
  const setAuth = useAuthStore((s) => s.setAuth);
  const [step, setStep] = useState<'form' | 'otp'>('form');
  const [loading, setLoading] = useState(false);
  const [companies, setCompanies] = useState<{ id: string; nom: string }[]>([]);
  const [otp, setOtp] = useState('');
  const [otpLoading, setOtpLoading] = useState(false);
  const [resending, setResending] = useState(false);

  const [profil, setProfil] = useState<ProfilType>('particulier');
  const [tenantId, setTenantId] = useState('');
  const [nom, setNom] = useState('');
  const [telephone, setTelephone] = useState('');
  const [email, setEmail] = useState('');
  // Vendeur social
  const [boutiqueNom, setBoutiqueNom] = useState('');
  const [ramassageVille, setRamassageVille] = useState('');
  const [ramassageQuartier, setRamassageQuartier] = useState('');
  const [codMoyen, setCodMoyen] = useState<CodMoyen>('momo');

  useEffect(() => {
    authService.getCompanies().then(setCompanies).catch(() => setCompanies([]));
  }, []);

  const phoneOk = isValidPhone(telephone);
  const canSubmit =
    !!tenantId &&
    nom.trim().length > 1 &&
    phoneOk &&
    (profil === 'particulier' || (boutiqueNom.trim() && ramassageVille.trim()));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    try {
      setLoading(true);
      await authService.registerExpediteur({
        nom: nom.trim(),
        telephone: telephone.replace(/[\s.-]/g, ''),
        tenant_id: tenantId,
        profil_type: profil,
        email: email.trim() || undefined,
        boutique_nom: profil === 'vendeur_social' ? boutiqueNom.trim() : undefined,
        // Adresse de ramassage par défaut — Vendeur Social (obligatoire) et
        // Particulier (facultatif, spéc. « Adresse par défaut »).
        ramassage_ville: ramassageVille.trim() || undefined,
        ramassage_quartier: ramassageQuartier.trim() || undefined,
        cod_reversement_moyen: profil === 'vendeur_social' ? codMoyen : undefined,
      });
      setStep('otp');
      toast.success('Code envoyé par SMS / WhatsApp.');
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Erreur lors de l'inscription");
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async () => {
    if (otp.length < 4) return;
    try {
      setOtpLoading(true);
      const response = await authService.verifyExpediteurOtp(
        telephone.replace(/[\s.-]/g, ''),
        otp,
      );
      setAuth(response.user as any, response.access_token!, response.refresh_token);
      toast.success('Compte activé !');
      navigate('/mes-envois');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Code invalide');
      setOtp('');
    } finally {
      setOtpLoading(false);
    }
  };

  const handleResend = async () => {
    try {
      setResending(true);
      await authService.requestExpediteurOtp(telephone.replace(/[\s.-]/g, ''));
      toast.success('Nouveau code envoyé.');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Erreur');
    } finally {
      setResending(false);
    }
  };

  const inputCls =
    'w-full pl-11 pr-4 py-3 bg-white/10 border border-white/30 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-white/50 backdrop-blur-md';

  return (
    <div className="min-h-screen relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-500">
        <div className="absolute top-20 left-20 w-72 h-72 bg-white/10 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-20 right-20 w-96 h-96 bg-teal-400/20 rounded-full blur-3xl animate-pulse delay-1000" />
      </div>

      <div className="relative z-10 min-h-screen flex items-center justify-center p-4">
        <div className="w-full max-w-md bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-8 shadow-2xl">
          <div className="flex flex-col items-center mb-6">
            <div className="w-14 h-14 bg-white/20 rounded-full flex items-center justify-center mb-3">
              <UserPlus className="w-7 h-7 text-white" />
            </div>
            <h1 className="text-xl font-bold text-white">Créer un compte expéditeur</h1>
            <p className="text-white/60 text-sm mt-1 text-center">
              Envoyez et suivez vos colis — inscription par téléphone, sans mot de passe
            </p>
          </div>

          {step === 'form' ? (
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Choix du profil */}
              <div className="grid grid-cols-2 gap-2">
                {([
                  ['particulier', 'Particulier', 'Envois personnels'],
                  ['vendeur_social', 'Vendeur social', 'WhatsApp / Insta / TikTok'],
                ] as [ProfilType, string, string][]).map(([val, label, hint]) => (
                  <button
                    type="button"
                    key={val}
                    onClick={() => setProfil(val)}
                    className={`p-3 rounded-xl border text-left transition ${
                      profil === val
                        ? 'border-white bg-white/20'
                        : 'border-white/30 bg-white/5 hover:bg-white/10'
                    }`}
                  >
                    <span className="block text-white text-sm font-semibold">{label}</span>
                    <span className="block text-white/50 text-[11px] leading-tight mt-0.5">{hint}</span>
                  </button>
                ))}
              </div>

              <div className="relative">
                <Building className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/60 pointer-events-none z-10" />
                <select
                  value={tenantId}
                  onChange={(e) => setTenantId(e.target.value)}
                  className="w-full pl-11 pr-4 py-3 bg-white/10 border border-white/30 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-white/50 backdrop-blur-md appearance-none cursor-pointer"
                >
                  <option value="" className="bg-gray-800 text-white">-- Transporteur --</option>
                  {companies.map((c) => (
                    <option key={c.id} value={c.id} className="bg-gray-800 text-white">{c.nom}</option>
                  ))}
                </select>
              </div>

              {profil === 'vendeur_social' && (
                <div className="relative">
                  <Store className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/60 pointer-events-none" />
                  <input
                    type="text"
                    placeholder="Nom de la boutique"
                    className={inputCls}
                    value={boutiqueNom}
                    onChange={(e) => setBoutiqueNom(e.target.value)}
                  />
                </div>
              )}

              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/60 pointer-events-none" />
                <input
                  type="text"
                  placeholder={profil === 'vendeur_social' ? 'Nom du responsable' : 'Nom complet'}
                  className={inputCls}
                  value={nom}
                  onChange={(e) => setNom(e.target.value)}
                />
              </div>

              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/60 pointer-events-none" />
                <input
                  type="tel"
                  placeholder="Téléphone (+237… ou international)"
                  className={inputCls}
                  value={telephone}
                  onChange={(e) => setTelephone(e.target.value)}
                />
                {telephone.trim() && !phoneOk && (
                  <p className="text-red-200 text-xs mt-1">Numéro invalide (format local ou +indicatif).</p>
                )}
              </div>

              {profil === 'particulier' && (
                <div className="grid grid-cols-2 gap-2">
                  <div className="relative">
                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/60 pointer-events-none" />
                    <input
                      type="text"
                      placeholder="Ville (adresse par défaut)"
                      className={inputCls}
                      value={ramassageVille}
                      onChange={(e) => setRamassageVille(e.target.value)}
                    />
                  </div>
                  <input
                    type="text"
                    placeholder="Quartier / repère"
                    className="w-full px-4 py-3 bg-white/10 border border-white/30 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-white/50 backdrop-blur-md"
                    value={ramassageQuartier}
                    onChange={(e) => setRamassageQuartier(e.target.value)}
                  />
                </div>
              )}

              {profil === 'vendeur_social' && (
                <>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="relative">
                      <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/60 pointer-events-none" />
                      <input
                        type="text"
                        placeholder="Ville ramassage"
                        className={inputCls}
                        value={ramassageVille}
                        onChange={(e) => setRamassageVille(e.target.value)}
                      />
                    </div>
                    <input
                      type="text"
                      placeholder="Quartier"
                      className="w-full px-4 py-3 bg-white/10 border border-white/30 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-white/50 backdrop-blur-md"
                      value={ramassageQuartier}
                      onChange={(e) => setRamassageQuartier(e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="flex items-center gap-2 text-white/70 text-xs mb-1.5">
                      <Wallet className="w-4 h-4" /> Reversement des encaissements (COD)
                    </label>
                    <div className="flex gap-2">
                      {COD_MOYENS.map((m) => (
                        <button
                          type="button"
                          key={m.value}
                          onClick={() => setCodMoyen(m.value)}
                          className={`flex-1 py-2 rounded-lg text-xs font-medium transition ${
                            codMoyen === m.value ? 'bg-white text-indigo-700' : 'bg-white/10 text-white/70'
                          }`}
                        >
                          {m.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              )}

              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/60 pointer-events-none" />
                <input
                  type="email"
                  placeholder="Adresse e-mail (optionnel)"
                  className={inputCls}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>

              <button
                type="submit"
                disabled={loading || !canSubmit}
                className="w-full py-3 bg-white text-indigo-700 font-semibold rounded-xl hover:bg-white/90 transition disabled:opacity-60"
              >
                {loading ? 'Création...' : 'Recevoir mon code'}
              </button>
            </form>
          ) : (
            <div className="space-y-4">
              <p className="text-white/80 text-sm text-center">
                Un code à 4 chiffres a été envoyé au <strong>{telephone}</strong> par SMS ou WhatsApp.
              </p>
              <input
                type="text"
                inputMode="numeric"
                maxLength={4}
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 4))}
                placeholder="0000"
                className="w-full text-center tracking-[0.5em] text-2xl py-3 bg-white/10 border border-white/30 rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-white/50"
              />
              <button
                onClick={handleVerify}
                disabled={otp.length < 4 || otpLoading}
                className="w-full py-3 bg-white text-indigo-700 font-semibold rounded-xl hover:bg-white/90 transition disabled:opacity-60"
              >
                {otpLoading ? 'Vérification...' : 'Vérifier et activer'}
              </button>
              <button
                onClick={handleResend}
                disabled={resending}
                className="w-full text-white/70 text-sm hover:text-white transition"
              >
                {resending ? 'Envoi...' : 'Code non reçu ? Renvoyer'}
              </button>
            </div>
          )}

          <p className="text-center text-white/60 text-sm mt-6">
            Déjà inscrit ?{' '}
            <Link to="/login" className="text-white font-medium hover:underline">
              Se connecter
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};
